import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Settings as SettingsCog,
  LayoutDashboard, Users, Factory, Package,
  Wallet, FileText, Briefcase, ClipboardList, RefreshCcw, ReceiptText,
  ShoppingCart, FilePen, Receipt, Truck,
  Warehouse, TrendingUp, BarChart3, FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from './context/AuthContext';
import { useOrgTier } from '../hooks/useOrgTier';
import { useMobileNav } from './context/MobileNavContext';
import { EInvoiceCredentialsDialog } from './EInvoiceCredentialsDialog';

// Map a nav item id (used in data-testids + screenshots) to its URL path.
// Keeping this in one place means the rest of the file can keep referring
// to the symbolic `id` while the router takes care of the URL.
const idToPath = {
  dashboard: '/dashboard',
  customers: '/customers',
  vendors: '/vendors',
  products: '/products',
  invoices: '/invoices',
  quotations: '/quotations',
  proforma: '/proforma',
  'credit-notes': '/credit-notes',
  einvoice: '/einvoice',
  'delivery-challans': '/sales/delivery-challans',
  'eway-bills': '/sales/eway-bills',
  'purchase-orders': '/purchase-orders',
  'purchase-invoices': '/purchase-invoices',
  'debit-notes': '/debit-notes',
  inventory: '/inventory',
  'reports-basic': '/reports/basic',
  'reports-gst': '/reports/gst-compliance',
  settings: '/settings',
};

export function Navigation() {
  const { user } = useAuth();
  const { isPro } = useOrgTier();
  const location = useLocation();
  const { open: drawerOpen, close: closeDrawer } = useMobileNav();
  const [einvoiceCredsOpen, setEinvoiceCredsOpen] = useState(false);

  // Resolve the active nav id from the current URL path. For nested
  // routes like /reports/basic we keep both segments so the sidebar's
  // child-link active-highlight matches the URL exactly.
  const pathParts = location.pathname.split('/').filter(Boolean);
  const firstSegment = pathParts[0] || 'dashboard';
  let activeId = firstSegment;
  if (firstSegment === 'reports') {
    const second = pathParts[1];
    activeId = second === 'gst-compliance' ? 'reports-gst' : 'reports-basic';
  } else if (firstSegment === 'sales') {
    // /sales/delivery-challans/* and /sales/eway-bills/* — keep the
    // active highlight on the matching sidebar child entry.
    const second = pathParts[1];
    if (second === 'eway-bills') activeId = 'eway-bills';
    else if (second === 'delivery-challans') activeId = 'delivery-challans';
  }

  const salesIds = [
    'invoices', 'quotations', 'proforma', 'credit-notes', 'einvoice',
    'delivery-challans', 'eway-bills',
  ];
  const purchaseIds = ['purchase-orders', 'purchase-invoices', 'debit-notes'];
  const reportIds = ['reports-basic', 'reports-gst'];

  // Collapsible sections auto-expand when one of their children is active.
  const [salesOpen, setSalesOpen] = useState(salesIds.includes(activeId));
  const [purchasesOpen, setPurchasesOpen] = useState(purchaseIds.includes(activeId));
  const [reportsOpen, setReportsOpen] = useState(reportIds.includes(activeId));

  // Keep the sections in sync if the URL changes externally (e.g. browser
  // back/forward, or a deep-link via Dashboard shortcuts).
  useEffect(() => {
    if (salesIds.includes(activeId)) setSalesOpen(true);
    if (purchaseIds.includes(activeId)) setPurchasesOpen(true);
    if (reportIds.includes(activeId)) setReportsOpen(true);
    // intentionally not collapsing on outward nav — user toggled state wins.
  }, [activeId]);

  const topItems = [
    { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
    { id: 'customers', label: 'Customers', Icon: Users },
    { id: 'vendors', label: 'Vendors', Icon: Factory },
    { id: 'products', label: 'Products', Icon: Package },
  ];

  // E-Invoice is a Pro-tier feature — hide it from the sidebar entirely
  // for Standard orgs (and let the route guard handle deep-links).
  // Delivery Challans visible to all; E-Way Bills Pro-only.
  const salesItems = [
    { id: 'invoices', label: 'Invoices', Icon: FileText },
    { id: 'quotations', label: 'Quotations', Icon: Briefcase },
    { id: 'proforma', label: 'Proforma Invoice', Icon: ClipboardList },
    { id: 'credit-notes', label: 'Credit Notes', Icon: RefreshCcw },
    { id: 'delivery-challans', label: 'Delivery Challans', Icon: Truck },
    ...(isPro
      ? [{ id: 'eway-bills', label: 'E-Way Bills', Icon: FileText }]
      : []),
    ...(isPro
      ? [{
          id: 'einvoice',
          label: 'E-Invoice',
          Icon: ReceiptText,
          extraAction: {
            title: 'E-Invoice credentials',
            onClick: () => setEinvoiceCredsOpen(true),
            testId: 'nav-einvoice-cog',
          },
        }]
      : []),
  ];

  const purchaseItems = [
    { id: 'purchase-orders', label: 'Purchase Orders', Icon: FilePen },
    { id: 'purchase-invoices', label: 'Purchase Invoices', Icon: Receipt },
    { id: 'debit-notes', label: 'Debit Notes', Icon: RefreshCcw },
  ];

  const reportsItems = [
    { id: 'reports-basic', label: 'Basic Reports', Icon: BarChart3 },
    { id: 'reports-gst', label: 'GST Reports', Icon: FileSpreadsheet },
  ];

  const bottomItems = [
    { id: 'inventory', label: 'Inventory', Icon: Warehouse },
  ];

  // Active item is now a soft rounded pill with theme-tinted background.
  // No more full-bleed right border — keeps the sidebar feeling lighter
  // and matches modern shell conventions (Linear / Notion / Vercel).
  const activeStyle = (isActive) =>
    isActive
      ? {
          backgroundColor: 'var(--theme-primary-light)',
          color: 'var(--theme-primary)',
        }
      : {};

  const NavLinkItem = ({ item, indent }) => {
    const { Icon } = item;
    return (
      <div className="relative group">
        <NavLink
          to={idToPath[item.id]}
          className={({ isActive }) =>
            `w-full flex items-center gap-3 ${indent ? 'pl-8 pr-3' : 'px-3'} py-3 min-h-[44px] rounded-lg transition-all duration-200 ${item.extraAction ? 'pr-10' : ''} ${isActive ? 'font-semibold' : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200 hover:text-slate-900'}`
          }
          style={({ isActive }) => activeStyle(isActive)}
          data-testid={`nav-${item.id}-btn`}
          end
        >
          <Icon
            className={indent ? 'h-4 w-4' : 'h-[18px] w-[18px]'}
            style={{ color: 'var(--theme-primary)' }}
            strokeWidth={2}
          />
          <span className="text-sm">{item.label}</span>
        </NavLink>
        {item.extraAction && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); item.extraAction.onClick(); }}
            title={item.extraAction.title}
            data-testid={item.extraAction.testId}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-theme-primary hover:bg-theme-primary-light transition-all duration-300 ease-out hover:scale-125 active:scale-95 [&>svg]:transition-transform [&>svg]:duration-500 [&>svg]:ease-out hover:[&>svg]:rotate-180"
          >
            <SettingsCog className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  const CollapsibleSection = ({ label, Icon, isOpen, onToggle, items, testId }) => {
    const hasActiveChild = items.some((i) => i.id === activeId);
    return (
      <div className="space-y-0.5">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-3 py-3 min-h-[44px] rounded-lg transition-all duration-200 hover:bg-slate-100 active:bg-slate-200"
          style={hasActiveChild && !isOpen ? { color: 'var(--theme-primary)' } : {}}
          data-testid={testId}
        >
          <div className="flex items-center gap-3">
            <Icon
              className="h-[18px] w-[18px]"
              style={{ color: 'var(--theme-primary)' }}
              strokeWidth={2}
            />
            <span className="text-sm font-medium text-slate-700">{label}</span>
          </div>
          <span
            className="text-lg font-bold leading-none transition-transform duration-200"
            style={{ color: 'var(--theme-primary)', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}
          >
            +
          </span>
        </button>
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out space-y-0.5"
          // Each NavLinkItem has min-h-[44px] + 2px spacing — using 48px
          // gives a small safety margin so the last item never gets clipped
          // (regression from when Sales hit 7 children and E-Invoice
          // disappeared off the bottom of the panel).
          style={{ maxHeight: isOpen ? `${items.length * 48}px` : '0px', opacity: isOpen ? 1 : 0 }}
        >
          {items.map((item) => (
            <NavLinkItem key={item.id} item={item} indent />
          ))}
        </div>
      </div>
    );
  };

  if (!user) return null;

  return (
    <>
      {/* Backdrop — only visible on mobile while the drawer is open.
          Tapping it dismisses the drawer (matches every native mobile
          pattern). Hidden on md+ where the drawer is always visible. */}
      <div
        aria-hidden={!drawerOpen}
        onClick={closeDrawer}
        className={`md:hidden fixed inset-0 top-14 bg-slate-900/60 backdrop-blur-sm z-30 transition-opacity duration-200 ${
          drawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-nav-backdrop"
      />
      <aside
        data-testid="sidebar"
        aria-hidden={!drawerOpen ? 'true' : undefined}
        className={`fixed left-0 top-14 bottom-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col z-40 transition-transform duration-200 ease-out
          ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0`}
      >
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
        {topItems.map((item) => (
          <NavLinkItem key={item.id} item={item} />
        ))}

        <CollapsibleSection
          label="Sales"
          Icon={Wallet}
          isOpen={salesOpen}
          onToggle={() => setSalesOpen(!salesOpen)}
          items={salesItems}
          testId="nav-sales-toggle"
        />

        <CollapsibleSection
          label="Purchases"
          Icon={ShoppingCart}
          isOpen={purchasesOpen}
          onToggle={() => setPurchasesOpen(!purchasesOpen)}
          items={purchaseItems}
          testId="nav-purchases-toggle"
        />

        {bottomItems.map((item) => (
          <NavLinkItem key={item.id} item={item} />
        ))}

        <CollapsibleSection
          label="Reports"
          Icon={TrendingUp}
          isOpen={reportsOpen}
          onToggle={() => setReportsOpen(!reportsOpen)}
          items={reportsItems}
          testId="nav-reports-toggle"
        />
      </nav>

      <EInvoiceCredentialsDialog
        open={einvoiceCredsOpen}
        onOpenChange={setEinvoiceCredsOpen}
      />
    </aside>
    </>
  );
}
